import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import type {
  DeactivatePoolRequest,
  DeactivationReason,
  Pool,
  PoolCache,
  PoolService,
  ReactivatePoolRequest,
} from '../types/pool';

export const EMPTY_POOL_CACHE: PoolCache = {
  totalPools: 0,
  poolsList: [],
  lastUpdated: null,
};

const db = getFirestore();

const toDate = (value: unknown): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  return undefined;
};

/**
 * Subscribe to the company's pool cache document
 * (companies/{companyId}/cache/pools). This is the primary read path for the
 * pools list - a single denormalised document covering every pool in the
 * company, mirroring the approach documented in
 * docs/technical/pool-active-inactive-status.md §5.3 (mobile target design).
 *
 * Falls back to an empty cache (rather than throwing) if the cache document
 * doesn't exist yet (e.g. a brand new company with zero pools - the cache is
 * only created by the onPoolCreated trigger on first write).
 */
export const subscribeToPoolCache = (
  companyId: string,
  callback: (cache: PoolCache) => void,
  onError?: (error: Error) => void
): (() => void) => {
  const cacheRef = doc(db, 'companies', companyId, 'cache', 'pools');

  return onSnapshot(
    cacheRef,
    (docSnap) => {
      if (!docSnap.exists()) {
        callback({ totalPools: 0, poolsList: [], lastUpdated: null });
        return;
      }

      const data = docSnap.data();
      callback({
        totalPools: data.totalPools ?? 0,
        poolsList: data.poolsList ?? [],
        lastUpdated: toDate(data.lastUpdated) ?? null,
      });
    },
    (error) => {
      console.error('Error subscribing to pool cache:', error);
      onError?.(error);
    }
  );
};

/**
 * One-shot fetch of the company's pool cache document, for manual
 * refreshCache() calls (as opposed to the live subscription above).
 */
export const getPoolCache = async (companyId: string): Promise<PoolCache> => {
  try {
    const cacheRef = doc(db, 'companies', companyId, 'cache', 'pools');
    const docSnap = await getDoc(cacheRef);

    if (!docSnap.exists()) {
      return EMPTY_POOL_CACHE;
    }

    const data = docSnap.data();
    return {
      totalPools: data.totalPools ?? 0,
      poolsList: data.poolsList ?? [],
      lastUpdated: toDate(data.lastUpdated) ?? null,
    };
  } catch (error) {
    console.error('Error getting pool cache:', error);
    throw new Error('Failed to get pool cache');
  }
};

/**
 * Get a single pool by ID from companies/{companyId}/pools/{poolId}.
 * All company members can read pools directly (see firestore.rules).
 */
export const getPool = async (
  companyId: string,
  poolId: string
): Promise<Pool | null> => {
  try {
    const docRef = doc(db, 'companies', companyId, 'pools', poolId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      deactivatedAt: toDate(data.deactivatedAt) ?? null,
      createdAt: toDate(data.createdAt) ?? new Date(),
      updatedAt: toDate(data.updatedAt) ?? new Date(),
    } as Pool;
  } catch (error) {
    console.error('Error getting pool:', error);
    throw new Error('Failed to get pool');
  }
};

/**
 * Subscribe to a single pool document for live updates (e.g. reflecting
 * deactivation/reactivation immediately on the detail page).
 */
export const subscribeToPool = (
  companyId: string,
  poolId: string,
  callback: (pool: Pool | null) => void,
  onError?: (error: Error) => void
): (() => void) => {
  const docRef = doc(db, 'companies', companyId, 'pools', poolId);

  return onSnapshot(
    docRef,
    (docSnap) => {
      if (!docSnap.exists()) {
        callback(null);
        return;
      }

      const data = docSnap.data();
      callback({
        id: docSnap.id,
        ...data,
        deactivatedAt: toDate(data.deactivatedAt) ?? null,
        createdAt: toDate(data.createdAt) ?? new Date(),
        updatedAt: toDate(data.updatedAt) ?? new Date(),
      } as Pool);
    },
    (error) => {
      console.error('Error subscribing to pool:', error);
      onError?.(error);
    }
  );
};

/**
 * Fetch recent pool service records for a pool (used for the Chemical
 * Readings and Service History cards on the pool detail page), most recent
 * first. Requires the pool.id + serviceDate composite index documented in
 * docs/technical/data-model.md.
 */
export const getRecentPoolServices = async (
  companyId: string,
  poolId: string,
  count = 5
): Promise<PoolService[]> => {
  try {
    const servicesRef = collection(db, 'companies', companyId, 'poolServices');
    // Matches the pool.id ASC + serviceDate DESC composite index documented
    // in docs/technical/data-model.md.
    const q = query(
      servicesRef,
      where('pool.id', '==', poolId),
      orderBy('serviceDate', 'desc'),
      limit(count)
    );

    const snapshot = await getDocs(q);

    return snapshot.docs.map((docSnap) => {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        serviceDate: toDate(data.serviceDate) ?? new Date(),
        createdAt: toDate(data.createdAt) ?? new Date(),
        updatedAt: toDate(data.updatedAt),
      } as PoolService;
    });
  } catch (error) {
    console.error('Error getting pool services:', error);
    throw new Error('Failed to get pool service history');
  }
};

// Fields supplied by the Add/Edit Pool form. Deliberately excludes
// isActive/deactivatedAt/deactivationReason(Text) - firestore.rules blocks
// changes to those fields on `update` (leavesDeactivationFieldsUnchanged()),
// and pool creation defaults isActive to true server-side via this client
// write, matching the mobile app's savePool behaviour.
export type PoolFormInput = {
  poolName: string;
  poolStyle: Pool['poolStyle'];
  poolType: Pool['poolType'];
  poolVolume: number;
  poolSize: string;
  poolStreetAddress: string;
  poolCity: string;
  poolState: string;
  customer: Pool['customer'];
  billingModel: Pool['billingModel'];
};

/**
 * Create a new pool under companies/{companyId}/pools. Direct client write,
 * gated by firestore.rules (`allow create: if isCompanyAdmin(companyId)`) -
 * there is no createPool Cloud Function, mirroring pool-guy-mobile's
 * savePool(). Callers must check hasPermission('canEditPools') before
 * invoking this. The onPoolCreated trigger keeps the pools/customers cache
 * documents in sync server-side.
 */
export const createPool = async (
  companyId: string,
  input: PoolFormInput,
  userId: string
): Promise<Pool> => {
  try {
    const poolsRef = collection(db, 'companies', companyId, 'pools');
    const now = serverTimestamp();

    const docRef = await addDoc(poolsRef, {
      ...input,
      isActive: true,
      deactivatedAt: null,
      deactivationReason: null,
      deactivationReasonText: null,
      createdAt: now,
      updatedAt: now,
      createdBy: userId,
      updatedBy: userId,
    });

    const created = await getPool(companyId, docRef.id);
    if (!created) {
      throw new Error('Failed to load pool after creation');
    }
    return created;
  } catch (error) {
    console.error('Error creating pool:', error);
    throw new Error('Failed to create pool');
  }
};

/**
 * Update an existing pool's editable fields. Direct client write, gated by
 * firestore.rules (`allow update: if isCompanyAdmin(companyId) &&
 * leavesDeactivationFieldsUnchanged()`) - status fields are intentionally
 * omitted here and must be changed via deactivatePool/reactivatePool on the
 * Pool Details page instead. Callers must check
 * hasPermission('canEditPools') before invoking this.
 */
export const updatePool = async (
  companyId: string,
  poolId: string,
  input: PoolFormInput,
  userId: string
): Promise<Pool> => {
  try {
    const poolRef = doc(db, 'companies', companyId, 'pools', poolId);

    await updateDoc(poolRef, {
      ...input,
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });

    const updated = await getPool(companyId, poolId);
    if (!updated) {
      throw new Error('Failed to load pool after update');
    }
    return updated;
  } catch (error) {
    console.error('Error updating pool:', error);
    throw new Error('Failed to update pool');
  }
};

const poolCallableErrorMessages: Record<string, string> = {
  'functions/invalid-argument': 'Invalid request. Please check your input.',
  'functions/permission-denied':
    'Only admins can change a pool’s active status.',
  'functions/not-found': 'Pool not found.',
  'functions/internal': 'Server error. Please try again later.',
};

const toPoolCallableError = (error: unknown, fallback: string): Error => {
  const errorCode =
    error instanceof Error && 'code' in error
      ? (error as { code: string }).code
      : '';
  const message = poolCallableErrorMessages[errorCode] ?? fallback;
  return new Error(message);
};

// Server-side operation (admin-only) - firestore.rules blocks direct client
// writes to isActive/deactivatedAt/deactivationReason(Text).
export const deactivatePool = async (
  companyId: string,
  poolId: string,
  deactivationReason: DeactivationReason,
  deactivationReasonText?: string
): Promise<void> => {
  try {
    const deactivateFunction = httpsCallable<
      DeactivatePoolRequest,
      { success: boolean }
    >(functions, 'deactivatePool');

    await deactivateFunction({
      companyId,
      poolId,
      deactivationReason,
      deactivationReasonText,
    });
  } catch (error) {
    console.error('Error deactivating pool:', error);
    throw toPoolCallableError(error, 'Failed to set pool to inactive.');
  }
};

// Server-side operation (admin-only) - no reason payload, immediate.
export const reactivatePool = async (
  companyId: string,
  poolId: string
): Promise<void> => {
  try {
    const reactivateFunction = httpsCallable<
      ReactivatePoolRequest,
      { success: boolean }
    >(functions, 'reactivatePool');

    await reactivateFunction({ companyId, poolId });
  } catch (error) {
    console.error('Error reactivating pool:', error);
    throw toPoolCallableError(error, 'Failed to set pool to active.');
  }
};

/**
 * Permanently delete a pool. Direct client write, gated by firestore.rules
 * (`allow delete: if isCompanyAdmin(companyId)`) - there is no deletePool
 * Cloud Function, mirroring pool-guy-mobile's deletePool(). Not transactional:
 * associated services/repairs are intentionally left in place (no cascading
 * delete). Callers must check hasPermission('canDeletePools') before invoking
 * this. The onPoolDeleted-equivalent cache sync (if any) happens server-side;
 * this function does not touch the pools cache document directly, matching
 * createPool/updatePool's convention in this file.
 */
export const deletePool = async (
  companyId: string,
  poolId: string
): Promise<void> => {
  try {
    // Existence pre-check, mirroring mobile's deletePool().
    const existing = await getPool(companyId, poolId);
    if (!existing) {
      throw new Error('Pool not found.');
    }

    const poolRef = doc(db, 'companies', companyId, 'pools', poolId);
    await deleteDoc(poolRef);
  } catch (error) {
    console.error('Error deleting pool:', error);
    throw error instanceof Error && error.message === 'Pool not found.'
      ? error
      : new Error('Failed to delete pool.');
  }
};
